// Permisos del navegador que usa la Consola: notificaciones y microfono (el telefono web).
// Primero se explica para que sirven; el cuadro del navegador solo aparece al tocar "Activar".
// Si la persona lo bloqueo, la tarjeta le ensena a desbloquearlo. Ubicacion y camara no se usan.
import { useEffect, useState, useSyncExternalStore } from 'react';

import { Icon, type IconName } from '../lib/icons';
import { toast } from './toasts';

export type TipoPermiso = 'notificaciones' | 'microfono';
export type EstadoPermiso = 'pendiente' | 'pidiendo' | 'concedido' | 'denegado' | 'nodisponible';

const INFO: Record<TipoPermiso, { t: string; d: string; icono: IconName; sitio: string }> = {
  notificaciones: {
    t: 'Notificaciones',
    d: 'Te avisamos de casos asignados, mensajes y llamadas aunque tengas otra pestaña abierta.',
    icono: 'bell',
    sitio: 'Notificaciones',
  },
  microfono: {
    t: 'Micrófono',
    d: 'Para hablar por el teléfono de la Consola. No grabamos nada sin que lo sepas.',
    icono: 'mic',
    sitio: 'Micrófono',
  },
};

const ETIQUETA: Record<EstadoPermiso, string> = {
  pendiente: 'Sin pedir',
  pidiendo: 'Esperando tu respuesta…',
  concedido: 'Activado',
  denegado: 'Bloqueado',
  nodisponible: 'No disponible aquí',
};

/* ---------- estado compartido (las tarjetas de Mi perfil y de Notificaciones se ven igual) ---------- */

const estados: Record<TipoPermiso, EstadoPermiso> = { notificaciones: 'pendiente', microfono: 'pendiente' };
const subs = new Set<() => void>();
const poner = (t: TipoPermiso, e: EstadoPermiso) => {
  if (estados[t] === e) return;
  estados[t] = e;
  subs.forEach((s) => s());
};
const desdeNavegador = (s: PermissionState | NotificationPermission): EstadoPermiso => (s === 'granted' ? 'concedido' : s === 'denied' ? 'denegado' : 'pendiente');

const hayNotif = () => typeof window !== 'undefined' && 'Notification' in window;
const hayMic = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

const vigilados = new Set<TipoPermiso>();
// Lee el estado real sin preguntar nada a la persona, y lo sigue si cambia en el candado.
export async function revisarPermiso(t: TipoPermiso) {
  if (t === 'notificaciones') {
    if (!hayNotif()) return poner(t, 'nodisponible');
    poner(t, desdeNavegador(Notification.permission));
  } else if (!hayMic()) return poner(t, 'nodisponible');
  if (estados[t] === 'pidiendo') return;
  try {
    const st = await navigator.permissions?.query({ name: (t === 'notificaciones' ? 'notifications' : 'microphone') as PermissionName });
    if (!st) return;
    poner(t, desdeNavegador(st.state));
    if (!vigilados.has(t)) {
      vigilados.add(t);
      st.addEventListener('change', () => {
        if (estados[t] !== 'pidiendo') poner(t, desdeNavegador(st.state));
      });
    }
  } catch {
    /* navegador sin permissions.query para este permiso: queda lo que se sabe */
  }
}

// Ahora si: el cuadro del navegador.
export async function pedirPermiso(t: TipoPermiso) {
  poner(t, 'pidiendo');
  try {
    if (t === 'notificaciones') {
      const r = await Notification.requestPermission();
      poner(t, desdeNavegador(r));
    } else {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((x) => x.stop());
      poner(t, 'concedido');
    }
  } catch (e) {
    const n = (e as DOMException)?.name;
    // Sin microfono conectado no es un bloqueo del permiso.
    if (n === 'NotFoundError') {
      poner(t, 'pendiente');
      toast('No encontramos un micrófono conectado.', 'error');
    } else poner(t, 'denegado');
  }
  if (estados[t] === 'concedido') toast(`${INFO[t].t}: activado.`);
}

export function usePermiso(t: TipoPermiso) {
  useEffect(() => {
    void revisarPermiso(t);
  }, [t]);
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => estados[t],
  );
}

// Escucha el microfono un momento para decir si llega sonido.
async function probarMicrofono(): Promise<'bien' | 'silencio'> {
  const s = await navigator.mediaDevices.getUserMedia({ audio: true });
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  try {
    if (!Ctx) return 'bien';
    const ctx = new Ctx();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(s).connect(an);
    const buf = new Uint8Array(an.fftSize);
    let pico = 0;
    const fin = performance.now() + 1500;
    while (performance.now() < fin) {
      await new Promise((r) => setTimeout(r, 60));
      an.getByteTimeDomainData(buf);
      for (const v of buf) pico = Math.max(pico, Math.abs(v - 128));
    }
    void ctx.close();
    return pico > 4 ? 'bien' : 'silencio';
  } finally {
    s.getTracks().forEach((x) => x.stop());
  }
}

export function TarjetaPermiso({ tipo, compacta }: { tipo: TipoPermiso; compacta?: boolean }) {
  const e = usePermiso(tipo);
  const p = INFO[tipo];
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState<string | null>(null);

  const probar = async () => {
    if (tipo === 'notificaciones') {
      try {
        new Notification('Consola XmarteK', { body: 'Así te llegará un aviso: Caso #2187 asignado a ti.' });
        setPrueba('Te enviamos un aviso de prueba.');
      } catch {
        setPrueba('El navegador no dejó mostrar el aviso de prueba.');
      }
      return;
    }
    setProbando(true);
    setPrueba(null);
    try {
      const r = await probarMicrofono();
      const t = r === 'bien' ? 'El micrófono funciona: te escuchamos.' : 'No escuchamos sonido. Revisa que el micrófono no esté silenciado.';
      setPrueba(t);
      toast(t, r === 'bien' ? 'ok' : 'info');
    } catch {
      setPrueba('No pudimos abrir el micrófono.');
      void revisarPermiso(tipo);
    } finally {
      setProbando(false);
    }
  };

  const revisar = async () => {
    await revisarPermiso(tipo);
    if (estados[tipo] === 'denegado') toast('Sigue bloqueado. Cámbialo en el candado y recarga la página.', 'info');
  };

  const boton =
    e === 'pendiente' ? (
      <button type="button" className="btn primary" onClick={() => void pedirPermiso(tipo)}>
        Activar
      </button>
    ) : e === 'pidiendo' ? (
      <button type="button" className="btn" disabled>
        Esperando respuesta…
      </button>
    ) : e === 'concedido' ? (
      <button type="button" className="btn" onClick={() => void probar()} disabled={probando}>
        {probando ? 'Escuchando… di algo' : 'Probar'}
      </button>
    ) : e === 'denegado' ? (
      <button type="button" className="btn" onClick={() => void revisar()}>
        Ya lo cambié, revisar
      </button>
    ) : null;

  return (
    <article className={`pm-t ${e}${compacta ? ' compacta' : ''}`} aria-live="polite">
      <div className="pm-cab">
        <div className="pm-sello" aria-hidden="true">
          <Icon name={p.icono} />
          {e === 'concedido' ? <i className="pm-ok">✓</i> : e === 'denegado' ? <i className="pm-no">!</i> : null}
        </div>
        <div className="pm-tit">
          <h3>{p.t}</h3>
          <p className="pm-est">{ETIQUETA[e]}</p>
        </div>
        {compacta ? boton : null}
      </div>
      {compacta && e !== 'denegado' ? null : <p className="pm-d">{e === 'nodisponible' ? 'Este navegador no lo permite, o la página no está en una dirección segura (https).' : p.d}</p>}
      {e === 'pidiendo' ? <p className="pm-pista">Arriba, junto a la dirección, el navegador te pregunta. Toca «Permitir».</p> : null}
      {e === 'denegado' ? (
        <ol className="pm-guia">
          <li>
            Toca el candado <Icon name="lock" size={13} /> junto a la dirección.
          </li>
          <li>Abre «Permisos del sitio» (o «Configuración del sitio»).</li>
          <li>Cambia {p.sitio} a «Permitir» y recarga la página.</li>
        </ol>
      ) : null}
      {prueba && e === 'concedido' ? <p className="pm-prueba">{prueba}</p> : null}
      {compacta ? null : boton}
    </article>
  );
}

export function PermisosNavegador() {
  return (
    <section className="box pm" style={{ gridColumn: '1 / -1' }}>
      <h2 className="lbl">Permisos del navegador</h2>
      <p className="sub" style={{ marginTop: 0 }}>
        Antes de pedírtelos, te contamos para qué. El navegador solo pregunta cuando tocas «Activar».
      </p>
      <div className="pm-g">
        <TarjetaPermiso tipo="notificaciones" />
        <TarjetaPermiso tipo="microfono" />
      </div>
    </section>
  );
}
